import { observable, when, reaction } from 'mobx';

import { createProviderStore as createInsuranceProviderStore } from '@ratehub/ins-common';

import { createViewStore } from '../stores/ViewStore';
import startHistoryWatcher from './startHistoryWatcher';


/**
 * @public
 * Create a new application context instance
 * @param {Object} arg
 * @param {SessionStore} arg.sessionStore
 * @param {Object} arg.insuranceProviders
 * @param {boolean} arg.includeRouting
 * @returns {Object}
 */

function createContext({ sessionStore, insuranceProviders, includeRouting, locale }) {
    if (!sessionStore) {
        throw new Error('sessionStore should be provided');
    }

    if (!locale) {
        throw new Error('locale should be provided');
    }

    const _historyWatcherDisposer = observable.box(null);

    // NOTE: built in 2-stages to allow bi-directional references.
    const self = observable({
        sessionStore,

        viewStore: null,
        savedApplicationStore: null,
        insuranceProviderStore: createInsuranceProviderStore({
            insurers: insuranceProviders?.insurers ?? [],
            brokers: insuranceProviders?.brokers ?? [],
        }),

        /**
         * If we are ready for interaction
         */
        get isReady() {
            return self.sessionStore.hasFetchedSession;
        },

        get isLoggedIn() {
            return self.sessionStore.isLoggedIn;
        },

        // show Sign-In modal when the user has not logged in ever
        get hasLoggedOut() {
            return self.sessionStore.hasLoggedOut;
        },

        get shouldShowSignInModal() {
            return !self.sessionStore.isLoggedIn
                && !self.sessionStore.isLoggingOut
                && !self.sessionStore.hasLoggedOut;
        },

        get firstName() {
            return self.sessionStore.profile.firstName;
        },

        get email() {
            return self.sessionStore.email;
        },

        /** If our routing behaviour is enabled  */
        get isRoutingEnabled() {
            return !!_historyWatcherDisposer.get();
        },
    });

    // Now that the context object is created, initialize our resources.
    self.viewStore = createViewStore({ locale }, self);

    // Attempting to start the router prior to being ready causes odd behaviour.
    if (includeRouting) {
        when(
            () => self.isReady,
            () => {
                _historyWatcherDisposer.set(startHistoryWatcher(self));
            },
            { name: 'Start routing when initialized' },
        );
    }

    // If they're ever not signed-in, show a sign-in modal.
    reaction(
        () => self.shouldShowSignInModal,
        (shouldShowSignInModal) => shouldShowSignInModal && self.viewStore.showSignIn(),
        { name: 'Show sign-in modal for guest user' },
    );

    return self;
}

export default createContext;
